import { useState } from "react";
import { CHANNEL_OPEN_CONFIG } from "@/shared/utils/utils";
import { getCampaignInsightTheme } from "@/shared/utils/helperFunctions";
import { Progress } from "@heroui/react";

const CampaignInsightCard = ({
  index,
  isActive,
  onToggle,
  title,
  value,
  icon,
  textColor,
  analytics,
  handleView,
  borderCard = false,
  interactionMode = "toggle",
  allowFirstCard = false,
  isDashboard = false,
  total,
  percentage,
  campaignStatus,
  expandable = true,
  hoverStats,
}) => {
  const [hovered, setHovered] = useState(false);

  const isHoverMode = interactionMode === "hover";
  const canExpand = expandable && (index !== 0 || allowFirstCard);
  const showAnalytics = canExpand && (isHoverMode ? hovered : isActive);

  const hoverHandlers =
    isHoverMode && canExpand
      ? {
          onMouseEnter: () => setHovered(true),
          onMouseLeave: () => setHovered(false),
        }
      : {};

  const handleClick = isHoverMode
    ? undefined
    : canExpand
      ? () => onToggle(index)
      : undefined;

  const theme = getCampaignInsightTheme(textColor);

  return (
    <div
      className={`w-full rounded-xl p-3 flex flex-col gap-1 transition-all duration-300
        ${canExpand ? "cursor-pointer hover:bg-gray-100/50 dark:hover:bg-gray-800/50" : ""}
        ${borderCard ? "border border-gray-200 dark:border-gray-800" : ""}
        ${showAnalytics ? "bg-gray-100/40 dark:bg-gray-800/40" : ""}
      `}
      onClick={handleClick}
      {...hoverHandlers}
    >
      {/* icon + value row — always visible */}
      <div className="flex gap-2 items-center">
        <div
          className={`rounded-md p-1.5 flex items-center justify-center ${theme.iconBgClass}`}
        >
          {icon}
        </div>
        <h4 className="text-xs font-semibold text-gray-800 dark:text-gray-200">
          {title?.toUpperCase()}
        </h4>
      </div>

      {/* progress bar with value and total */}
      <div className="flex flex-col gap-1">
        <div className="flex items-baseline gap-4">
          <div className="flex items-baseline gap-0.5">
            <span className={`text-xl font-semibold text-gray-800 dark:text-gray-100`}>
              {value}
            </span>
            {total && percentage && (
              <span className="text-sm text-gray-500 dark:text-gray-400">/</span>
            )}
            {total && percentage && (
              <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">{total}</span>
            )}
          </div>

          {(percentage || campaignStatus) && (
            <div
              className={`flex items-center justify-center rounded-lg py-0.5 px-2 ${theme.badgeBgClass}`}
            >
              <span className={`text-[10px] font-medium ${theme.textClass}`}>
                {percentage
                  ? percentage
                  : campaignStatus?.charAt(0).toUpperCase() +
                    campaignStatus?.slice(1)}
              </span>
            </div>
          )}
        </div>
        <Progress
          value={value}
          color={theme.progressColor}
          total={total}
          maxValue={total}
          minValue={0}
          showValueLabel={false}
          classNames={{
            base: "w-full h-[3px]",
            indicator: theme.progressColor,
            track: "bg-gray-200 dark:bg-gray-700",
          }}
        />
      </div>

      {/* Metric context — always visible for clarity */}
      {/* <div className="mt-1 overflow-visible">
        <p className="text-[11px] leading-snug text-gray-600 font-normal tracking-wide">
          {description}
        </p>
      </div> */}

      {/* analytics by channel — expands below description */}
      <div
        className={`overflow-hidden transition-all duration-300 ${
          showAnalytics
            ? "max-h-96 opacity-100 translate-y-0 mt-1"
            : "max-h-0 opacity-0 translate-y-2"
        }`}
      >
        <div className="flex flex-col gap-1.5">
          {hoverStats?.map((stat) => (
            <div
              key={stat.label}
              className="flex items-center justify-between rounded-md bg-white dark:bg-gray-900/90 border border-gray-100 dark:border-gray-800 p-1.5 text-xs text-gray-800 dark:text-gray-100"
            >
              <span>{stat.label}</span>
              <span className="flex items-center gap-2">
                <strong>{stat.value}</strong>
                {stat.value > 0 && stat.action && !isDashboard && (
                  <button
                    type="button"
                    className="text-[11px] text-secondary dark:text-secondary-light hover:underline"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleView(stat.channel, stat.action);
                    }}
                  >
                    View
                  </button>
                )}
              </span>
            </div>
          ))}
          {!hoverStats && Object.entries(CHANNEL_OPEN_CONFIG)
            .filter(([key]) => title !== "Clicked" || key === "email")
            .map(([key, config]) => {
              const Icon = config.icon;

              const sent = analytics?.[key]?.sent || 0;
              const opens = analytics?.[key]?.opened || 0;
              const clicked = analytics?.[key]?.clicked || 0;
              const responded = analytics?.[key]?.responded || 0;
              const showEmailBreakdown =
                key === "email" &&
                ["Sent", "Opened", "Clicked", "Responded"].includes(title);
              const initialMetric = analytics?.[key]?.[`initial${title}`] || 0;
              const followupMetric = analytics?.[key]?.[`followup${title}`] || 0;

              const statValue =
                title === "Sent"
                  ? sent
                  : title === "Opened"
                    ? opens
                    : title === "Clicked"
                      ? clicked
                      : responded;

              const statLabel =
                title === "Sent"
                  ? "sents"
                  : title === "Opened"
                    ? "opens"
                    : title === "Clicked"
                      ? "clicks"
                      : "replies";

              return (
                <div
                  key={key}
                  className="grid grid-cols-[1.5rem_minmax(0,1fr)_auto] items-center gap-2 text-xs text-gray-800 dark:text-gray-100 bg-white dark:bg-gray-900/90 border border-gray-100 dark:border-gray-800 rounded-md w-full p-1"
                >
                  <span className="flex items-center justify-center">{Icon}</span>
                  {showEmailBreakdown ? (
                    <span className="grid min-w-0 grid-cols-[auto_minmax(0,1fr)] items-start gap-1.5">
                      <span className="pt-1 font-medium text-gray-700 dark:text-gray-200">
                        Email
                      </span>
                      <span className="flex min-w-0 flex-wrap items-center gap-1">
                        <span className="whitespace-nowrap rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-1.5 py-0.5 text-[10.5px] text-gray-500 dark:text-gray-400">
                          Initial{" "}
                          <strong className="text-gray-800 dark:text-gray-100">
                            {initialMetric}
                          </strong>
                        </span>
                        <span className="whitespace-nowrap rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-1.5 py-0.5 text-[10.5px] text-gray-500 dark:text-gray-400">
                          Follow-up{" "}
                          <strong className="text-gray-800 dark:text-gray-100">
                            {followupMetric}
                          </strong>
                        </span>
                      </span>
                    </span>
                  ) : (
                    <span className="min-w-0 truncate text-gray-700 dark:text-gray-200">
                      {config.label} {statLabel} : {statValue}
                    </span>
                  )}
                  {statValue > 0 && !isDashboard ? (
                    <span
                      className="shrink-0 text-secondary text-[11px] hover:underline cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleView(key, title);
                      }}
                    >
                      View
                    </span>
                  ) : (
                    <span />
                  )}
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
};

export default CampaignInsightCard;
